import { SpendPickers } from '../../budget/components/SpendPickers';
import { useAccounts, useBudgetSettings } from '../../budget/hooks';
import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import {
  DEFAULT_PAYMENTS_SETTINGS,
  findDue,
  isOpen,
  paymentPayFrom,
  type Due,
  type PayeeLike,
} from '@tahi/core';
import {
  payDue,
  payExtra,
  setDueAmount,
  setDueMinimum,
  setDueStatus,
  undoPayment,
} from '../../../data/commands/payments';
import { useCommandEnv, useUsable } from '../../../data/hooks';
import { Button, Money, Sheet, showToast } from '../../../ui';
import { TextField } from '../../../ui/TextField';
import { useGoBack } from '../../spending/hooks';
import { copy, errorText } from '../copy';
import { dayShort, minorToInput, parsePesoInput, partialText, peso } from '../format';
import { PAYMENTS_BASE, usePaymentsSettings, usePaymentsSnapshot, useToday } from '../hooks';

type Mode = 'choose' | 'other' | 'amount' | 'minimum';

function Breakdown({ due }: { due: Due }) {
  const c = due.card;
  if (!c) return null;
  const rows = [
    [copy.opening, c.openingMinor],
    [copy.carried, c.carriedInMinor],
    [copy.purchases, c.purchasesMinor],
    [copy.installments, c.installmentsMinor],
  ] as const;
  return (
    <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-sm">
      {rows
        .filter(([, v]) => v !== 0)
        .map(([label, v]) => (
          <div key={label} className="contents">
            <dt className="text-muted">{label}</dt>
            <dd className="text-right font-mono tabular-nums text-fg">{peso(v)}</dd>
          </div>
        ))}
      <dt className="text-muted">Minimum{c.minimumEstimated ? ` (${copy.estimated})` : ''}</dt>
      <dd className="text-right font-mono tabular-nums text-fg">{peso(c.minimumMinor)}</dd>
    </dl>
  );
}

/** Pay sheet for one due (/p/:id/due/:period), or any payment on a loan or card (/p/:id/pay). */
export function PayRoute() {
  const { id = '', period } = useParams();
  const [params] = useSearchParams();
  const env = useCommandEnv();
  const usable = useUsable();
  const today = useToday();
  const snap = usePaymentsSnapshot();
  const goBack = useGoBack(`${PAYMENTS_BASE}/p/${id}`);
  const different = params.get('different') === '1';
  const [mode, setMode] = useState<Mode>(different ? 'other' : 'choose');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(today);
  const [whole, setWhole] = useState(different);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [accountId, setAccountId] = useState<string | null>(null);
  const paySettings = usePaymentsSettings();
  const budgetSettings = useBudgetSettings();
  const accounts = useAccounts();

  if (!snap) return null;
  const payee: PayeeLike | undefined = snap.payees.find((p) => p.id === id && !p.deletedAt);
  const due = payee && period ? findDue(snap, id, period, today) : null;
  const close = (open: boolean) => !open && goBack();
  if (!payee || (period && !due)) {
    return (
      <Sheet open onOpenChange={close} title={copy.pay}>
        <p className="text-muted">{copy.notFound}</p>
      </Sheet>
    );
  }

  async function run(
    fn: () => Promise<{ ok: boolean; error?: string } | void>,
    after?: () => void,
  ) {
    setBusy(true);
    setError(null);
    try {
      const r = await fn();
      if (r && !r.ok) setError(errorText(r.error ?? ''));
      else after?.();
    } finally {
      setBusy(false);
    }
  }

  function pay(amountMinor: number, setAmountToo = false) {
    void run(async () => {
      const r = due
        ? await payDue(env, id, due.period, {
            amountMinor,
            paidDate: date,
            setAmount: setAmountToo,
            ...(accountId ? { accountId } : {}),
          })
        : await payExtra(env, id, {
            amountMinor,
            paidDate: date,
            ...(accountId ? { accountId } : {}),
          });
      if (!r.ok) return r;
      const left = 'remainingMinor' in r ? (r.remainingMinor as number) : 0;
      showToast({
        message: left > 0 && !due?.transfer ? copy.partialToast(left) : copy.paidToast(amountMinor),
        action: { label: copy.undo, onPress: () => void undoPayment(env, r.paymentId) },
      });
      goBack();
      return r;
    });
  }

  const typed = parsePesoInput(amount);
  // v0.7.4: a payee without its own account pays from Payments' default, else Budget's "Pay from".
  const fallbackPayFrom = paymentPayFrom(
    null,
    null,
    paySettings ?? DEFAULT_PAYMENTS_SETTINGS,
    budgetSettings ?? { useFunds: false, defaultAccountId: null },
    true,
    accounts ?? [],
  );
  const holder = payee.kind === 'card' ? payee.cardholder : null;
  const title = due
    ? holder
      ? copy.payShare(holder)
      : `${copy.pay} ${payee.name}`
    : `${copy.payAnyAmount} · ${payee.name}`;
  const open = due ? isOpen(due) || (due.transfer && due.status === 'rolled') : true;
  const canSkip =
    due &&
    due.payments.length === 0 &&
    (payee.kind === 'bill' || payee.kind === 'utility' || payee.loanType === 'flexible');

  const amountForm = (label: string, onSave: (minor: number) => void, extra?: React.ReactNode) => (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (typed !== null) onSave(typed);
      }}
    >
      <TextField
        label={copy.amount}
        inputMode="decimal"
        autoComplete="off"
        autoFocus
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
      />
      {extra}
      <Button type="submit" variant="primary" block disabled={typed === null || busy}>
        {label}
      </Button>
      <Button variant="ghost" block onClick={() => setMode('choose')}>
        {copy.cancel}
      </Button>
    </form>
  );

  const dateField = (
    <TextField
      label={copy.paidOn}
      type="date"
      value={date}
      max={today}
      onChange={(e) => e.target.value && setDate(e.target.value)}
    />
  );

  return (
    <Sheet open onOpenChange={close} title={title}>
      <div className="flex flex-col gap-4">
        {due ? (
          <div className="flex flex-col gap-1">
            <Money
              minor={open ? due.remainingMinor : due.amountMinor}
              cents="auto"
              className="text-[32px] leading-tight"
            />
            <p className="text-sm text-muted">
              {[
                payee.name,
                `Due ${dayShort(due.dueDate)}`,
                due.estimated ? copy.estimated : null,
                due.status === 'partial' ? partialText(due) : null,
                due.status === 'paid' ? 'Paid' : null,
                due.status === 'skipped' ? 'Skipped' : null,
                due.status === 'closed' ? 'Closed' : null,
                due.status === 'rolled' ? copy.rolled : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
        ) : null}
        {due?.transfer ? (
          <>
            <Breakdown due={due} />
            <p className="text-sm text-muted">{copy.transferNote}</p>
          </>
        ) : null}
        {!usable('spending') ? <p className="text-sm text-muted">{copy.noSpending}</p> : null}
        {usable('spending') && usable('budget') && open ? (
          <SpendPickers
            withFund={false}
            fundId={null}
            onFund={() => undefined}
            accountId={accountId}
            onAccount={setAccountId}
            fallbackAccountId={payee.accountId ?? fallbackPayFrom}
          />
        ) : null}
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}

        {mode === 'choose' && open ? (
          <div className="flex flex-col gap-2">
            {dateField}
            {due && due.remainingMinor > 0 ? (
              <Button
                variant="primary"
                block
                disabled={busy}
                onClick={() => pay(due.remainingMinor)}
              >
                {copy.payFull(due.remainingMinor)}
              </Button>
            ) : null}
            {due?.card &&
            due.card.minimumMinor > due.paidMinor &&
            due.card.minimumMinor < due.amountMinor ? (
              <Button
                variant="secondary"
                block
                disabled={busy}
                onClick={() => pay(due.card!.minimumMinor - due.paidMinor)}
              >
                {copy.payMinimum(due.card.minimumMinor - due.paidMinor)}
              </Button>
            ) : null}
            <Button
              variant={due ? 'secondary' : 'primary'}
              block
              onClick={() => {
                setAmount(
                  due && payee.kind === 'utility' && due.estimated
                    ? minorToInput(due.remainingMinor)
                    : '',
                );
                setWhole(Boolean(due && payee.kind === 'utility' && due.estimated));
                setMode('other');
              }}
            >
              {due ? copy.payOther : copy.pay}
            </Button>
            {due && payee.kind === 'utility' && due.estimated ? (
              <Button variant="ghost" block onClick={() => setMode('amount')}>
                {copy.enterAmount}
              </Button>
            ) : null}
            {due?.transfer ? (
              <>
                <Button variant="ghost" block onClick={() => setMode('amount')}>
                  {copy.realStatement}
                </Button>
                <Button variant="ghost" block onClick={() => setMode('minimum')}>
                  {copy.realMinimum}
                </Button>
              </>
            ) : null}
            {due?.status === 'partial' && !due.transfer ? (
              <Button
                variant="ghost"
                block
                disabled={busy}
                onClick={() =>
                  void run(
                    () => setDueStatus(env, id, due.period, 'closed'),
                    () => {
                      showToast({ message: copy.closedToast });
                      goBack();
                    },
                  )
                }
              >
                {copy.closeRest}
              </Button>
            ) : null}
            {canSkip ? (
              <Button
                variant="ghost"
                block
                disabled={busy}
                onClick={() =>
                  void run(
                    () => setDueStatus(env, id, due.period, 'skipped'),
                    () => {
                      showToast({
                        message: copy.skippedToast,
                        action: {
                          label: copy.undo,
                          onPress: () => void setDueStatus(env, id, due.period, 'open'),
                        },
                      });
                      goBack();
                    },
                  )
                }
              >
                {copy.skip}
              </Button>
            ) : null}
          </div>
        ) : null}

        {mode === 'other'
          ? amountForm(
              copy.pay,
              (minor) => pay(minor, whole),
              <>
                {dateField}
                {due && !due.transfer ? (
                  <label className="flex min-h-11 items-center gap-3 text-sm text-fg">
                    <input
                      type="checkbox"
                      className="h-5 w-5 accent-[var(--accent)]"
                      checked={whole}
                      onChange={(e) => setWhole(e.target.checked)}
                    />
                    {copy.thatsTheWholeBill}
                  </label>
                ) : null}
              </>,
            )
          : null}
        {mode === 'amount' && due
          ? amountForm(
              copy.save,
              (minor) =>
                void run(
                  () => setDueAmount(env, id, due.period, minor),
                  () => setMode('choose'),
                ),
            )
          : null}
        {mode === 'minimum' && due
          ? amountForm(
              copy.save,
              (minor) =>
                void run(
                  () => setDueMinimum(env, id, due.period, minor),
                  () => setMode('choose'),
                ),
            )
          : null}

        {due && (due.status === 'skipped' || due.status === 'closed') ? (
          <Button
            variant="secondary"
            block
            disabled={busy}
            onClick={() => void run(() => setDueStatus(env, id, due.period, 'open'))}
          >
            {copy.reopen}
          </Button>
        ) : null}

        {due && due.payments.length > 0 ? (
          <section aria-label={copy.payments} className="flex flex-col gap-1">
            <h3 className="text-xs uppercase tracking-[0.06em] text-muted">{copy.payments}</h3>
            <ul className="flex flex-col">
              {due.payments.map((p) => (
                <li key={p.id} className="flex min-h-11 items-center justify-between gap-2">
                  <span className="text-sm text-fg">
                    {peso(p.amountMinor)} · {dayShort(p.paidDate)}
                  </span>
                  <Button
                    variant="quiet-destructive"
                    disabled={busy}
                    onClick={() => void run(() => undoPayment(env, p.id))}
                  >
                    {copy.undo}
                  </Button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>
    </Sheet>
  );
}
